import * as Popover from "@radix-ui/react-popover";
import { Check, Plus, Search, X } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";

// ── Types ────────────────────────────────────────────────────────────────────

export interface UserOption {
  id: number | string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  userName?: string;
  group?: string;
}

interface MultiUserSelectProps {
  users: UserOption[];
  value: UserOption[];
  onValueChange: (users: UserOption[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  compact?: boolean;
  maxSelected?: number;
  chipStyle?: boolean;
  densityMode?: "form" | "sidebar";
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "bg-[#e0e7ff] text-[#4f46e5]",
  "bg-[#bae6fd] text-[#0369a1]",
  "bg-[#d1fae5] text-[#047857]",
  "bg-[#fef3c7] text-[#b45309]",
  "bg-[#fce7f3] text-[#be185d]",
  "bg-[#ede9fe] text-[#7c3aed]",
  "bg-[#cffafe] text-[#0e7490]",
  "bg-[#ffedd5] text-[#c2410c]",
  "bg-[#fce7f3] text-[#9d174d]",
  "bg-[#dbeafe] text-[#1d4ed8]",
];

function getAvatarColor(id: string | number): string {
  const n = parseInt(String(id), 10);
  const idx = isNaN(n)
    ? String(id)
        .split("")
        .reduce((acc, c) => acc + c.charCodeAt(0), 0)
    : n;
  return AVATAR_COLORS[Math.abs(idx) % AVATAR_COLORS.length];
}

function getInitial(
  firstName?: string,
  lastName?: string,
  name?: string,
  userName?: string,
  email?: string,
): string {
  const source =
    firstName?.trim() ||
    name?.trim() ||
    lastName?.trim() ||
    userName?.trim() ||
    email?.trim();
  return source?.[0]?.toUpperCase() ?? "?";
}

function getDisplayName(user: UserOption): string {
  const full = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  if (full) return full;
  if (user.name && !/^\d+$/.test(user.name)) return user.name;
  if (user.userName && !/^\d+$/.test(user.userName)) return user.userName;
  return user.email || String(user.id);
}

function capitalize(str: string) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function normalizeName(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return trimmed;
  const isAllCaps = trimmed === trimmed.toUpperCase();
  if (!isAllCaps) return trimmed;
  return trimmed
    .toLowerCase()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

// ── Component ────────────────────────────────────────────────────────────────

export function MultiUserSelect({
  users,
  value,
  onValueChange,
  placeholder = "No one — assign someone",
  disabled = false,
  className,
  triggerClassName,
  compact = false,
  maxSelected,
  chipStyle = false,
  densityMode = "form",
}: MultiUserSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedIds = useMemo(
    () => new Set(value.map((u) => String(u.id))),
    [value],
  );

  const filteredUsers = useMemo(() => {
    const list = !search.trim()
      ? users
      : users.filter((u) => {
          const name = getDisplayName(u).toLowerCase();
          const email = (u.email ?? "").toLowerCase();
          const q = search.toLowerCase();
          return name.includes(q) || email.includes(q);
        });
    return [...list].sort((a, b) =>
      getDisplayName(a).localeCompare(getDisplayName(b)),
    );
  }, [users, search]);

  const handleToggle = (user: UserOption) => {
    const id = String(user.id);
    if (selectedIds.has(id)) {
      onValueChange(value.filter((u) => String(u.id) !== id));
    } else {
      if (maxSelected === 1) {
        onValueChange([user]);
        return;
      }
      if (typeof maxSelected === "number" && value.length >= maxSelected) {
        return;
      }
      onValueChange([...value, user]);
    }
  };

  const handleRemove = (e: React.MouseEvent, userId: string | number) => {
    e.preventDefault();
    e.stopPropagation();
    onValueChange(value.filter((u) => String(u.id) !== String(userId)));
  };

  // ── chipStyle: trigger button + chips rendered below (outside popover trigger) ──
  if (chipStyle) {
    const _atMax =
      typeof maxSelected === "number" &&
      maxSelected > 1 &&
      value.length >= maxSelected;
    return (
      <div className={cn("w-full", className)}>
        <Popover.Root
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setSearch("");
          }}
        >
          <Popover.Trigger asChild disabled={disabled}>
            <button
              type="button"
              className={cn(
                "flex w-full min-w-0 items-center justify-between transition-colors",
                "hover:border-ring/60 hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                disabled && "pointer-events-none opacity-50",
                value.length === 0
                  ? "text-muted-foreground"
                  : "text-foreground",
                triggerClassName,
              )}
            >
              <span className="min-w-0 flex-1 truncate text-left text-[12.5px]">
                {value.length === 0
                  ? placeholder
                  : value.length === 1
                    ? normalizeName(capitalize(getDisplayName(value[0])))
                    : `${value.length} people selected`}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0 text-muted-foreground/70"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              side="bottom"
              align="start"
              sideOffset={4}
              style={{ width: 288, zIndex: 1000000 }}
              className="rounded-lg border border-border bg-popover shadow-lg outline-none animate-in fade-in-0 zoom-in-95"
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
              onWheel={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <Search size={14} className="shrink-0 text-muted-foreground" />
                <input
                  ref={inputRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Type to filter…"
                  className="flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-muted-foreground"
                />
              </div>
              <div className="max-h-64 overflow-y-auto overscroll-contain py-1">
                {filteredUsers.length === 0 ? (
                  <p className="px-3 py-6 text-center text-[12.5px] text-muted-foreground">
                    No matching users
                  </p>
                ) : (
                  (() => {
                    // Group users
                    const grouped: Record<string, UserOption[]> = {};
                    const ungrouped: UserOption[] = [];
                    filteredUsers.forEach((u) => {
                      if (u.group) {
                        if (!grouped[u.group]) grouped[u.group] = [];
                        grouped[u.group].push(u);
                      } else {
                        ungrouped.push(u);
                      }
                    });

                    const renderUser = (user: UserOption) => {
                      const isSelected = selectedIds.has(String(user.id));
                      return (
                        <button
                          key={user.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleToggle(user)}
                          className={cn(
                            "flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] transition-colors hover:bg-accent",
                            isSelected && "bg-accent/50",
                          )}
                        >
                          <span
                            className={cn(
                              "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                              isSelected
                                ? "border-primary bg-primary text-white"
                                : "border-muted-foreground/30 bg-background",
                            )}
                          >
                            {isSelected && <Check size={10} strokeWidth={3} />}
                          </span>
                          <span
                            className={cn(
                              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-medium",
                              getAvatarColor(user.id),
                            )}
                          >
                            {getInitial(
                              user.firstName,
                              user.lastName,
                              user.name,
                              user.userName,
                              user.email,
                            )}
                          </span>
                          <div className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate font-normal text-foreground">
                              {normalizeName(capitalize(getDisplayName(user)))}
                            </span>
                            {user.email && (
                              <span className="truncate text-xs text-muted-foreground">
                                {user.email}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    };

                    return (
                      <>
                        {ungrouped.map(renderUser)}
                        {Object.entries(grouped).map(
                          ([groupName, groupUsers]) => (
                            <div key={groupName} className="mt-1">
                              <div className="px-3 py-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                {groupName}
                              </div>
                              {groupUsers.map(renderUser)}
                            </div>
                          ),
                        )}
                      </>
                    );
                  })()
                )}
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        {/* Chips rendered BELOW the trigger */}
        {value.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {value.map((user) => (
              <span
                key={user.id}
                className="inline-flex h-6 items-center gap-1.5 rounded-full border border-border/50 bg-muted px-2 text-[12px] font-medium text-foreground hover:bg-muted/80 transition-colors"
              >
                {/* Avatar circle */}
                <span
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-medium",
                    getAvatarColor(user.id),
                  )}
                >
                  {getInitial(
                    user.firstName,
                    user.lastName,
                    user.name,
                    user.userName,
                    user.email,
                  )}
                </span>
                <span className="max-w-[112px] truncate leading-none text-[12px]">
                  {normalizeName(capitalize(getDisplayName(user)))}
                </span>
                <button
                  type="button"
                  onClick={(e) => handleRemove(e, user.id)}
                  className="ml-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-background/60 transition-colors"
                  title={`Remove ${getDisplayName(user)}`}
                >
                  <X size={9} strokeWidth={2.5} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <Popover.Trigger asChild disabled={disabled}>
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          className={cn(
            "w-full cursor-pointer rounded-md transition-colors",
            disabled && "pointer-events-none opacity-50",
            className,
          )}
        >
          {compact ? (
            densityMode === "sidebar" ? (
              <div className="flex w-full items-center justify-end gap-1 py-0.5">
                <div className="flex items-center">
                  {value.length === 0 ? (
                    <span className="text-xs text-muted-foreground">None</span>
                  ) : maxSelected === 1 ? (
                    <span className="inline-flex h-7 max-w-[152px] items-center gap-1.5 rounded-full border border-border/70 bg-background pl-1 pr-2 text-xs font-normal text-foreground shadow-sm">
                      <span
                        className={cn(
                          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-medium",
                          getAvatarColor(value[0].id),
                        )}
                      >
                        {getInitial(
                          value[0].firstName,
                          value[0].lastName,
                          value[0].name,
                          value[0].userName,
                          value[0].email,
                        )}
                      </span>
                      <span className="truncate text-[12.5px]">
                        {normalizeName(capitalize(getDisplayName(value[0])))}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => handleRemove(e, value[0].id)}
                        className="ml-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        title={`Remove ${getDisplayName(value[0])}`}
                      >
                        <X size={9} strokeWidth={2.5} />
                      </button>
                    </span>
                  ) : (
                    <>
                      {value.slice(0, 4).map((user, idx) => (
                        <span
                          key={user.id}
                          className={cn(
                            "group relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-medium",
                            idx > 0 && "-ml-1.5",
                            getAvatarColor(user.id),
                          )}
                        >
                          {getInitial(
                            user.firstName,
                            user.lastName,
                            user.name,
                            user.userName,
                            user.email,
                          )}
                          <button
                            type="button"
                            onClick={(e) => handleRemove(e, user.id)}
                            className="absolute -right-1 -top-1 z-10 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-border bg-background text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
                            title={`Remove ${getDisplayName(user)}`}
                          >
                            <X size={8} strokeWidth={2.5} />
                          </button>
                          <span className="pointer-events-none invisible absolute bottom-full z-50 mb-1 -translate-x-1/2 left-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2 py-1 text-xs font-medium text-popover-foreground shadow-sm opacity-0 transition-opacity duration-150 group-hover:visible group-hover:opacity-100">
                            {normalizeName(capitalize(getDisplayName(user)))}
                          </span>
                        </span>
                      ))}
                      {value.length > 4 && (
                        <span className="-ml-1.5 flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-medium text-muted-foreground">
                          +{value.length - 4}
                        </span>
                      )}
                    </>
                  )}
                </div>
                <span
                  className="ml-0.5 flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted transition-colors"
                  title={value.length ? "Edit assignees" : "Add assignee"}
                >
                  <Plus size={11} />
                </span>
              </div>
            ) : (
              <div className="flex items-center justify-end gap-1 py-0.5 w-full">
                <div className="flex items-center">
                  {value.slice(0, 4).map((user, idx) => (
                    <span
                      key={user.id}
                      title={capitalize(getDisplayName(user))}
                      className={cn(
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-medium",
                        idx > 0 && "-ml-1.5",
                        getAvatarColor(user.id),
                      )}
                    >
                      {getInitial(
                        user.firstName,
                        user.lastName,
                        user.name,
                        user.userName,
                        user.email,
                      )}
                    </span>
                  ))}
                  {value.length > 4 && (
                    <span className="-ml-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
                      +{value.length - 4}
                    </span>
                  )}
                </div>
                <span
                  className="ml-1 flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted transition-colors"
                  title={value.length ? "Edit assignees" : "Add assignee"}
                >
                  <Plus size={12} />
                </span>
              </div>
            )
          ) : value.length === 0 ? (
            <p className="py-1 text-[13px] text-muted-foreground italic">
              {placeholder}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 py-0.5">
              {value.map((user) => (
                <div
                  key={user.id}
                  className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-muted border border-border/50 hover:bg-muted/80 transition-colors"
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-medium",
                      getAvatarColor(user.id),
                    )}
                  >
                    {getInitial(
                      user.firstName,
                      user.lastName,
                      user.name,
                      user.userName,
                      user.email,
                    )}
                  </span>
                  <span className="truncate text-[12px] font-normal text-foreground">
                    {capitalize(getDisplayName(user))}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => handleRemove(e, user.id)}
                    className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-background/60 transition-colors"
                    title="Remove"
                  >
                    <X size={10} strokeWidth={2.5} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          style={{ width: 288, zIndex: 1000000 }}
          className="rounded-lg border border-border bg-popover shadow-lg outline-none animate-in fade-in-0 zoom-in-95"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          onWheel={(e) => e.stopPropagation()}
        >
          {/* Search */}
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search size={14} className="shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type to filter…"
              className="flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-muted-foreground"
            />
          </div>

          {/* User list */}
          <div className="max-h-64 overflow-y-auto overscroll-contain py-1">
            {filteredUsers.length === 0 ? (
              <p className="px-3 py-6 text-center text-[12.5px] text-muted-foreground">
                No matching users
              </p>
            ) : (
              (() => {
                const grouped: Record<string, UserOption[]> = {};
                const ungrouped: UserOption[] = [];
                filteredUsers.forEach((u) => {
                  if (u.group) {
                    if (!grouped[u.group]) grouped[u.group] = [];
                    grouped[u.group].push(u);
                  } else {
                    ungrouped.push(u);
                  }
                });

                const renderUser = (user: UserOption) => {
                  const isSelected = selectedIds.has(String(user.id));
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleToggle(user)}
                      className={cn(
                        "flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] transition-colors hover:bg-accent",
                        isSelected && "bg-accent/50",
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                          isSelected
                            ? "border-primary bg-primary text-white"
                            : "border-muted-foreground/30 bg-background",
                        )}
                      >
                        {isSelected && <Check size={10} strokeWidth={3} />}
                      </span>
                      <span
                        className={cn(
                          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-medium",
                          getAvatarColor(user.id),
                        )}
                      >
                        {getInitial(
                          user.firstName,
                          user.lastName,
                          user.name,
                          user.userName,
                          user.email,
                        )}
                      </span>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="truncate font-normal text-foreground">
                          {capitalize(getDisplayName(user))}
                        </span>
                        {user.email && (
                          <span className="truncate text-xs text-muted-foreground">
                            {user.email}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                };

                return (
                  <>
                    {ungrouped.map(renderUser)}
                    {Object.entries(grouped).map(([groupName, groupUsers]) => (
                      <div key={groupName} className="mt-1">
                        <div className="px-3 py-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                          {groupName}
                        </div>
                        {groupUsers.map(renderUser)}
                      </div>
                    ))}
                  </>
                );
              })()
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
